import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ShoppingBag, Truck, CheckCircle2, Clock, UserRound } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
};

export default function CartsTab() {
  const { API, authHeaders, logout } = useAuth();
  const [carts, setCarts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/carts`, { headers: authHeaders });
      setCarts(data);
    } catch (e) { if (e.response?.status === 401) { toast.error("Session expired"); logout(); } }
    finally { setLoading(false); }
  }, [API, authHeaders, logout]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (loading) return <div className="grid place-items-center rounded-2xl border border-nosh-border bg-white py-20"><Loader2 className="h-6 w-6 animate-spin text-nosh-green" /></div>;
  if (!carts.length) return (
    <div className="grid place-items-center rounded-2xl border border-nosh-border bg-white py-20 text-center">
      <ShoppingBag className="h-8 w-8 text-nosh-ink/30" />
      <p className="mt-3 font-body text-nosh-ink/55">No cart activity yet. When visitors add dishes or leave their details, they'll appear here.</p>
    </div>
  );

  return (
    <div className="overflow-hidden rounded-2xl border border-nosh-border bg-white">
      <div className="hidden grid-cols-[1.4fr_1.6fr_0.8fr_0.7fr_1fr_1fr] gap-3 border-b border-nosh-border bg-nosh-surface px-5 py-3 font-poster text-[10px] font-bold uppercase tracking-widest text-nosh-ink/50 lg:grid">
        <span>Contact</span><span>Items</span><span>Fulfillment</span><span>Total</span><span>Status</span><span>Last activity</span>
      </div>
      {carts.map((c) => {
        const hasContact = c.name || c.email || c.phone;
        const status = c.converted ? "Converted" : hasContact ? "Lead" : "Abandoned";
        const statusStyle = c.converted ? "bg-nosh-green/10 text-nosh-green" : hasContact ? "bg-nosh-gold/15 text-nosh-gold" : "bg-nosh-ink/5 text-nosh-ink/50";
        const StatusIcon = c.converted ? CheckCircle2 : hasContact ? UserRound : Clock;
        return (
          <div key={c.session_id} data-testid={`cart-row-${c.session_id}`} className="grid grid-cols-1 gap-3 border-b border-nosh-border/60 px-5 py-4 last:border-0 lg:grid-cols-[1.4fr_1.6fr_0.8fr_0.7fr_1fr_1fr] lg:items-center">
            <div className="min-w-0">
              <div className="font-display text-base font-bold text-nosh-ink">{c.name || "Anonymous visitor"}</div>
              <div className="truncate font-body text-xs text-nosh-ink/55">{[c.phone, c.email].filter(Boolean).join(" · ") || "No details left"}</div>
            </div>
            <div className="min-w-0 font-body text-sm text-nosh-ink/70">
              {c.items?.length ? (
                <span className="line-clamp-2">{c.items.map((i) => `${i.qty}× ${i.name} (${i.size})`).join(", ")}</span>
              ) : <span className="text-nosh-ink/40">No dishes added</span>}
            </div>
            <div>
              {c.fulfillment && (
                <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-poster text-[10px] font-bold uppercase tracking-wide ${c.fulfillment === "Delivery" ? "bg-blue-50 text-blue-700" : "bg-nosh-green/10 text-nosh-green"}`}>
                  {c.fulfillment === "Delivery" ? <Truck className="h-3 w-3" /> : <ShoppingBag className="h-3 w-3" />}{c.fulfillment}
                </span>
              )}
            </div>
            <div className="font-display text-lg font-bold text-nosh-green">${c.total || 0}</div>
            <div>
              <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 font-poster text-[10px] font-bold uppercase tracking-wide ${statusStyle}`}>
                <StatusIcon className="h-3 w-3" />{status}
              </span>
            </div>
            <div className="font-body text-xs text-nosh-ink/55">{fmt(c.updated_at)}</div>
          </div>
        );
      })}
    </div>
  );
}
